/**
 * Full-page Kin agent at /apps/:slug/agent.
 *
 * Escape hatch for the preview lockout: the floating widget is
 * delivered by injecting a script tag into the preview's HTML, so a
 * preview that redirects, crashes, or serves non-HTML never shows the
 * agent — and the manager has no way to ask Kin to fix it. This page
 * mounts the same widget bundle (served by the portal at
 * /_kin/preview-widget.js, same origin) in its standalone full-page
 * mode, talking to the same AppWorkspace socket. No preview required.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiOrigin } from '../api/client.js';
import { AppTabs } from '../components/AppTabs.js';
import { IconExternal } from '../components/Icons.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { AnchorButton, Banner, PageHeader, PageSkeleton } from '../components/ui.js';
import { useAppPage } from '../lib/useAppPage.js';

const WIDGET_SRC = '/_kin/preview-widget.js';

interface KinWidgetApi {
  mountStandalone(
    container: HTMLElement,
    opts: {
      slug: string;
      appName: string;
      apiBase: string;
      appBase: string;
      previewUrl?: string | null;
    },
  ): { unmount: () => void };
}

declare global {
  interface Window {
    __kinPreviewWidget?: KinWidgetApi;
  }
}

// One in-flight load per SPA session; reset on failure so a re-visit
// to the page retries instead of caching the rejection forever.
let widgetLoad: Promise<KinWidgetApi> | null = null;

function loadWidget(): Promise<KinWidgetApi> {
  const existing = window.__kinPreviewWidget;
  if (existing) return Promise.resolve(existing);
  widgetLoad ??= new Promise<KinWidgetApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WIDGET_SRC;
    script.onload = () => {
      const api = window.__kinPreviewWidget;
      if (api) resolve(api);
      else reject(new Error('The Kin agent bundle loaded but did not initialize.'));
    };
    script.onerror = () => reject(new Error('Failed to load the Kin agent bundle.'));
    document.head.appendChild(script);
  });
  widgetLoad.catch(() => {
    widgetLoad = null;
  });
  return widgetLoad;
}

export function AppAgent() {
  const { slug } = useParams();
  const { app, loading, error } = useAppPage(slug);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mountError, setMountError] = useState<string | null>(null);

  const previewUrl = app?.cf_custom_domain_preview
    ? `https://${app.cf_custom_domain_preview}`
    : null;
  const canMount = !!app && app.is_manager && app.kind !== 'external';

  useEffect(() => {
    if (!canMount || !app) return;
    const el = containerRef.current;
    if (!el) return;
    let disposed = false;
    let handle: { unmount: () => void } | null = null;
    loadWidget()
      .then((api) => {
        if (disposed) return;
        handle = api.mountStandalone(el, {
          slug: app.slug,
          appName: app.name,
          apiBase: apiOrigin(),
          appBase: window.location.hostname,
          previewUrl,
        });
      })
      .catch((err: unknown) => {
        if (!disposed) setMountError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      disposed = true;
      handle?.unmount();
    };
    // `app` identity is stable after the initial load — this page never
    // calls reload() — so the socket mounts exactly once per app.
  }, [canMount, app, previewUrl]);

  if (loading) return <PageSkeleton rows={6} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app) return null;
  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Agent"
        description="Chat with Kin to edit this app without opening the preview."
      />
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        eyebrow="Edit with Kin"
        title="Agent"
        description="Chat with Kin to edit this app — works even when the preview won't load."
        actions={
          previewUrl ? (
            <AnchorButton
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              variant="secondary"
              size="sm"
            >
              Open preview
              <IconExternal size={13} />
            </AnchorButton>
          ) : undefined
        }
      />
      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0">
          {app.kind === 'external' ? (
            <Banner tone="info">
              External apps aren&apos;t built with Kin, so there&apos;s no agent workspace here.
            </Banner>
          ) : (
            <>
              {mountError && <Banner tone="error">{mountError}</Banner>}
              <div
                ref={containerRef}
                className="h-[max(480px,calc(100dvh-21rem))]"
                aria-label={`Kin agent for ${app.name}`}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
